import { useEffect, useRef, useState } from "react";
import { Camera } from "lucide-react";
import { Button } from "@/components/ax/Button";
import { getProfilePhoto, uploadProfilePhoto } from "@/lib/accountProfile";
import { useI18n } from "@/i18n";
import styles from "./AccountVisual.module.css";

export function ProfilePhoto({ initials }) {
  const { t } = useI18n();
  const picker = useRef(null), alive = useRef(true);
  const [photo, setPhoto] = useState(null), [candidate, setCandidate] = useState(null);
  const [preview, setPreview] = useState(null), [loading, setLoading] = useState(true), [busy, setBusy] = useState(false), [error, setError] = useState(null);
  const load = async () => {
    setLoading(true); setError(null);
    const result = await getProfilePhoto();
    if (alive.current) { if (result.ok) setPhoto(result.photo); else setError(t("acc.photoReadFailed")); setLoading(false); }
  };
  useEffect(() => { alive.current = true; load(); return () => { alive.current = false; }; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!candidate) { setPreview(null); return; }
    const url = URL.createObjectURL(candidate); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [candidate]);
  const choose = (event) => {
    const file = event.target.files?.[0]; event.target.value = "";
    if (!file) return;
    setError(null);
    if (!file.size || !["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 2 * 1024 * 1024) { setCandidate(null); setError(t("acc.photoInvalid")); return; }
    setCandidate(file);
  };
  const save = async () => {
    if (busy || !candidate) return;
    setBusy(true); setError(null);
    const result = await uploadProfilePhoto(candidate);
    if (alive.current) { if (result.ok) { setPhoto(result.photo); setCandidate(null); } else setError(t("acc.photoWriteFailed")); setBusy(false); }
  };
  return (
    <div className={styles.photoControl} data-testid="profile-photo-control">
      <span className={styles.avatar} aria-hidden="true">{preview || photo ? <img src={preview || photo} alt="" onError={() => { if (preview) { setCandidate(null); setPreview(null); setError(t("acc.photoInvalid")); } else { setPhoto(null); setError(t("acc.photoReadFailed")); } }} data-testid="profile-photo-image" /> : initials}</span>
      <input ref={picker} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={choose} disabled={busy || loading} data-testid="profile-photo-input" />
      <Button variant="ghost" className={styles.photoButton} onClick={() => picker.current?.click()} disabled={busy || loading} data-testid="profile-photo-choose"><Camera size={14} aria-hidden="true" />{loading ? t("acc.photoLoading") : t("acc.photoChange")}</Button>
      {candidate && <div className={styles.photoActions}><Button onClick={save} busy={busy} disabled={busy} data-testid="profile-photo-save">{t("acc.photoSave")}</Button><Button variant="ghost" disabled={busy} onClick={() => { setCandidate(null); setError(null); }} data-testid="profile-photo-cancel">{t("acc.photoCancel")}</Button></div>}
      {error && <p role="alert" className={styles.photoError} data-testid="profile-photo-error">{error} {!candidate && <button type="button" onClick={load} disabled={loading || busy}>{t("acc.retry")}</button>}</p>}
    </div>
  );
}
